
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRoute, useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import Multiselect from '@vueform/multiselect';
  import moment from 'moment';

  export default defineComponent({
    name: 'promotion-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
      Multiselect,
    },
    async setup() {
      const { t, te } = useI18n();
      const { can } = useAbility();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const promotionData = ref<any>(null);
      const selectedPackageId = ref<number>(0);
      const selectedInfluencerId = ref<number>(0);
      const allInfluencers = computed(
        () => store.getters.allInfluencersList || []
      );

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('promotionUpdating'), [
          translate('promotions'),
        ]);
        store.dispatch(Actions.GET_ALL_PACKAGES);
        store.dispatch(Actions.GET_ALL_INFLUENCERS);
      });

      const packages = computed(() => store.getters.allPackagesList);

      const { data } = await store.dispatch(
        Actions.GET_PROMOTION,
        route.params.id
      );
      promotionData.value = data.data;
      selectedPackageId.value = promotionData.value.packageId ?? null;
      selectedInfluencerId.value = promotionData.value.influencerId ?? null;
      promotionData.value.expiryAt = promotionData.value.expiryAt
        ? moment(promotionData.value.expiryAt).format('YYYY-MM-DD')
        : null;

      const promotionSchema = Yup.object().shape({
        titleAr: Yup.string().required(translate('AR_TITLE_IS_REQUIRED')),
        titleEn: Yup.string().required(translate('EN_TITLE_IS_REQUIRED')),
        promoCode: Yup.string().required(translate('PROMO_CODE_REQUIRED')),
        discountPercentage: Yup.number().required(
          translate('DISCOUNT_PERCENTAGE_REQUIRED')
        ),
        allowedUsage: Yup.number().required(
          translate('ALLOWED_USAGE_REQUIRED')
        ),
        duration: Yup.number().required(translate('DURATION_REQUIRED')),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        values.packageId = selectedPackageId.value;
        if (values.packageId != null) {
          values.packageId = Number(values.packageId);
        } else {
          values.packageId = null;
        }

        if (values.influencerId != null) {
          values.influencerId = Number(values.influencerId);
        } else {
          values.influencerId = null;
        }

        if (values.discountPercentage)
          values.discountPercentage = Number(values.discountPercentage);
        if (values.allowedUsage)
          values.allowedUsage = Number(values.allowedUsage);
        if (values.duration) values.duration = Number(values.duration);
        if (values.expiryAt) {
          values.expiryAt = new Date(values.expiryAt).toISOString();
        }

        await store.dispatch(Actions.UPDATE_PROMOTION, {
          id: promotionData.value?.id,
          data: values,
        });

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgain'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          if (submitButton.value) submitButton.value.disabled = false;
        } else {
          Swal.fire({
            text: translate('PROMOTION_UPDATE_SUCCESS'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            submitButton.value?.removeAttribute('data-kt-indicator');
            if (submitButton.value) submitButton.value.disabled = false;
            router.push({ name: 'promotions-listing' });
          });
        }
      };

      return {
        promotionData,
        submitButton,
        onSubmitUpdate,
        translate,
        packages,
        promotionSchema,
        selectedPackageId,
        goBack,
        can,
        allInfluencers,
        selectedInfluencerId,
      };
    },
  });
